/**
 * Error Telemetry Helper
 *
 * TASK-007: Centralized error reporting with UX analytics and Sentry integration
 * UNIFIED-OBS-001: Integrated with Event Journal for critical error tracking
 *
 * Provides unified error tracking across the application:
 * - Tracks UX events via trackUX(UX_EVENTS.ERROR_SHOWN)
 * - Sends critical errors to Sentry (real-time alerts)
 * - Logs critical errors to Event Journal (investigations)
 * - Maintains error type taxonomy
 */

import { trackUX, UX_EVENTS } from './ux-events';
import { logAppEvent } from '@/lib/telemetry/logAppEvent';
import { getSessionId, getRequestId } from '@/lib/session';
import { getRecentUserActions } from './journey-buffer';

// Conditional Sentry access (loaded by sentry.init.ts)
function getSentry() {
  try {
    // @ts-ignore - Sentry may not be loaded
    return window.__SENTRY__;
  } catch {
    return null;
  }
}

/**
 * Canonical error types for consistent tracking
 * 
 * These types are used in both UX analytics and Sentry tags
 */
export const ERROR_TYPES = {
  // Auth errors
  AUTH_MAGIC_LINK_FAILED: 'auth_magic_link_failed',
  AUTH_SESSION_EXPIRED: 'auth_session_expired',
  AUTH_UNAUTHORIZED: 'auth_unauthorized',
  
  // Onboarding/KYC errors
  KYC_START_FAILED: 'kyc_start_failed',
  KYC_REJECTED: 'kyc_rejected',
  KYC_RATE_LIMITED: 'kyc_rate_limited',
  ONBOARDING_SAVE_FAILED: 'onboarding_save_failed',
  
  // Wallet errors
  WALLET_BALANCES_FAILED: 'wallet_balances_failed',
  WALLET_TRANSFER_FAILED: 'wallet_transfer_failed',
  WALLET_QUOTE_FAILED: 'wallet_quote_failed',
  
  // Transaction errors
  TX_HISTORY_FAILED: 'tx_history_failed',
  TX_EXPORT_FAILED: 'tx_export_failed',
  
  // Network errors
  NETWORK_ERROR: 'network_error',
  TIMEOUT_ERROR: 'timeout_error',
  RATE_LIMITED: 'rate_limited',
  
  // System errors
  ERROR_BOUNDARY: 'error_boundary',
  UNKNOWN_ERROR: 'unknown_error',
} as const;

export type ErrorType = (typeof ERROR_TYPES)[keyof typeof ERROR_TYPES];

/**
 * Error types that should always be sent to Sentry (critical errors)
 */
const CRITICAL_ERROR_TYPES: ReadonlySet<ErrorType> = new Set([
  ERROR_TYPES.AUTH_UNAUTHORIZED,
  ERROR_TYPES.KYC_REJECTED,
  ERROR_TYPES.WALLET_TRANSFER_FAILED,
  ERROR_TYPES.ERROR_BOUNDARY,
  ERROR_TYPES.NETWORK_ERROR,
  ERROR_TYPES.TIMEOUT_ERROR,
]);

/**
 * Context for error reporting
 *
 * PRD-ADM-JOURNEY-001 Phase 3: Enhanced with journey tracking fields
 */
export interface ErrorContext {
  /**
   * Error type from ERROR_TYPES taxonomy
   */
  type: ErrorType;

  /**
   * Surface where error occurred (e.g., 'dashboard', 'wallet', 'transactions')
   */
  surface: string;

  /**
   * Whether this is a critical error (always sent to Sentry)
   */
  critical?: boolean;

  /**
   * Additional metadata for debugging
   */
  metadata?: Record<string, unknown>;

  /**
   * User ID (if available)
   */
  userId?: string;

  // ↓ Journey Tracking Fields (PRD-ADM-JOURNEY-001 Phase 3)

  /**
   * Session ID from journey tracking
   * Automatically populated if not provided
   */
  sessionId?: string;

  /**
   * Request ID from journey tracking
   * Automatically populated if not provided
   */
  requestId?: string | null;

  /**
   * Recent user actions from journey buffer
   * Automatically populated if not provided
   */
  recentUserActions?: Array<{
    event_type: string;
    timestamp: string;
    details: any;
  }>;
}

/**
 * Report UX error with analytics and Sentry integration
 * 
 * @example
 * ```ts
 * try {
 *   await loadWalletBalances();
 * } catch (error) {
 *   reportUxError(error, {
 *     type: ERROR_TYPES.WALLET_BALANCES_FAILED,
 *     surface: 'wallet',
 *     critical: false,
 *   });
 * }
 * ```
 */
export function reportUxError(
  error: unknown,
  context: ErrorContext
): void {
  const {
    type,
    surface,
    critical = false,
    metadata,
    userId,
    sessionId: providedSessionId,
    requestId: providedRequestId,
    recentUserActions: providedActions,
  } = context;

  // Auto-populate journey context if not provided (PRD-ADM-JOURNEY-001 Phase 3)
  let sessionId = providedSessionId;
  let requestId = providedRequestId;
  let recentUserActions = providedActions;

  try {
    if (!sessionId && typeof window !== 'undefined') {
      sessionId = getSessionId();
    }
    if (!requestId && typeof window !== 'undefined') {
      requestId = getRequestId();
    }
    if (!recentUserActions && typeof window !== 'undefined') {
      const rawActions = getRecentUserActions(10); // Last 10 actions
      recentUserActions = rawActions.map((action) => ({
        event_type: action.event,
        timestamp: new Date(action.ts).toISOString(),
        details: action.payload,
      }));
    }
  } catch (journeyError) {
    // Silent fail - don't break error reporting if journey tracking fails
    if (import.meta.env.DEV) {
      console.warn('[error-telemetry] Journey context population failed:', journeyError);
    }
  }

  // 1. Track UX event (always)
  trackUX(UX_EVENTS.ERROR_SHOWN, {
    type,
    surface,
    critical,
  });

  // 2. Send to Sentry if critical or in critical types list
  const shouldSendToSentry = critical || CRITICAL_ERROR_TYPES.has(type);

  if (shouldSendToSentry && typeof window !== 'undefined') {
    const Sentry = getSentry();
    if (!Sentry) return; // Sentry not loaded

    try {
      // Set user context if available
      if (userId) {
        Sentry.setUser({ id: userId });
      }

      // Capture exception with journey context (PRD-ADM-JOURNEY-001 Phase 3)
      Sentry.captureException(error, {
        tags: {
          error_type: type,
          surface,
          critical: String(critical),
          session_id: sessionId || 'unknown',
          request_id: requestId || 'unknown',
        },
        extra: {
          ...metadata,
          error_type: type,
          surface,
          // Journey tracking context
          session_id: sessionId,
          request_id: requestId,
          recent_user_actions: recentUserActions,
        },
        level: critical ? 'error' : 'warning',
      });
    } catch (sentryError) {
      // Silent fail - don't break app if Sentry fails
      if (import.meta.env.DEV) {
        console.warn('[error-telemetry] Sentry capture failed:', sentryError);
      }
    }
  }

  // 3. Log to Event Journal if critical (for investigations)
  if (shouldSendToSentry) {
    logAppEvent({
      eventType: `error.${type}`,
      actorType: userId ? 'user' : 'system',
      actorId: userId || null,
      entityType: surface,
      entityId: (metadata?.entityId as string) || null,
      severity: critical ? 'error' : 'warning',
      source: 'frontend',
      // Journey tracking context (PRD-ADM-JOURNEY-001 Phase 3)
      sessionId: sessionId || undefined,
      requestId: requestId || undefined,
      payload: {
        error_type: type,
        surface,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        // Include recent user actions for debugging
        recent_user_actions: recentUserActions,
        ...metadata,
      },
    }).catch((logError) => {
      // Silent fail - don't break app if event logging fails
      if (import.meta.env.DEV) {
        console.warn('[error-telemetry] Event Journal logging failed:', logError);
      }
    });
  }

  // 4. Log in dev mode
  if (import.meta.env.DEV) {
    console.error(`[error-telemetry] ${type} on ${surface}:`, error, metadata);
  }
}

/**
 * Track error retry action
 * 
 * @example
 * ```ts
 * const handleRetry = () => {
 *   trackErrorRetry(ERROR_TYPES.WALLET_BALANCES_FAILED, 'wallet');
 *   refetch();
 * };
 * ```
 */
export function trackErrorRetry(
  errorType: ErrorType,
  surface: string
): void {
  trackUX(UX_EVENTS.ERROR_RETRY_CLICKED, {
    type: errorType,
    surface,
  });
  
  if (import.meta.env.DEV) {
    console.log(`[error-telemetry] Retry clicked for ${errorType} on ${surface}`);
  }
}

/**
 * Map HTTP status code to error type
 * 
 * Helper for API error handling
 */
export function getErrorTypeFromHttpStatus(status: number): ErrorType {
  switch (status) {
    case 401:
      return ERROR_TYPES.AUTH_UNAUTHORIZED;
    case 403:
      return ERROR_TYPES.AUTH_UNAUTHORIZED;
    case 404:
      return ERROR_TYPES.UNKNOWN_ERROR;
    case 429:
      return ERROR_TYPES.RATE_LIMITED;
    case 500:
    case 502:
    case 503:
    case 504:
      return ERROR_TYPES.NETWORK_ERROR;
    default:
      return ERROR_TYPES.UNKNOWN_ERROR;
  }
}

/**
 * Determine if error is critical based on type
 */
export function isCriticalError(errorType: ErrorType): boolean {
  return CRITICAL_ERROR_TYPES.has(errorType);
}

/**
 * Set Sentry user context
 * 
 * Call this when user logs in or session is established
 */
export function setSentryUserContext(userId: string, email?: string): void {
  const Sentry = getSentry();
  if (!Sentry) return; // Sentry not loaded

  try {
    Sentry.setUser({
      id: userId,
      email: email,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[error-telemetry] Failed to set Sentry user context:', error);
    }
  }
}

/**
 * Set Sentry context for additional debugging info
 */
export function setSentryContext(key: string, value: unknown): void {
  const Sentry = getSentry();
  if (!Sentry) return; // Sentry not loaded

  try {
    Sentry.setContext(key, { value });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[error-telemetry] Failed to set Sentry context:', error);
    }
  }
}

